import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { Paginated } from '@/shared/types/common.types';
import { mergeRequestService } from '../services/mergeRequestService';
import type {
  DiffQuery,
  MergeRequestDiff,
  ModelBaselines,
  Participant,
  ProductionRequestInput,
  RevertInput,
} from '../types/baseline.types';
import type {
  DecisionInput,
  MergeRequest,
  MergeRequestAuditEntry,
  MergeRequestModel,
  MergeRequestQuery,
  MergeRequestSummary,
} from '../types/mergeRequest.types';

export const mergeRequestKeys = {
  all: ['mergeRequests'] as const,
  feature: ['mergeRequests', 'feature'] as const,
  models: ['mergeRequests', 'models'] as const,
  baselines: (modelId: string) => ['mergeRequests', 'baselines', modelId] as const,
  participants: (modelId: string) => ['mergeRequests', 'participants', modelId] as const,
  diff: (id: string, query: DiffQuery) => ['mergeRequests', 'diff', id, query] as const,
  list: (query: MergeRequestQuery) => ['mergeRequests', 'list', query] as const,
  detail: (id: string) => ['mergeRequests', 'detail', id] as const,
  auditTrail: (id: string) => ['mergeRequests', 'audit', id] as const,
};

/** `false` means Core has the feature off; the pages then say so instead of showing a 404 error. */
export function useMergeRequestFeatureQuery(): UseQueryResult<boolean> {
  return useQuery({
    queryKey: mergeRequestKeys.feature,
    queryFn: () => mergeRequestService.isEnabled(),
    staleTime: 5 * 60_000,
  });
}

/** The caller's models for the picker; only asked once the feature is known to be on. */
export function useMergeRequestModelsQuery(
  enabled: boolean
): UseQueryResult<readonly MergeRequestModel[]> {
  return useQuery({
    queryKey: mergeRequestKeys.models,
    queryFn: () => mergeRequestService.listModels(),
    enabled,
  });
}

export function useMergeRequestsQuery(
  query: MergeRequestQuery | null
): UseQueryResult<Paginated<MergeRequestSummary>> {
  return useQuery({
    queryKey: mergeRequestKeys.list(query ?? { modelId: '', page: 0, pageSize: 0 }),
    queryFn: () => {
      if (query === null) throw new Error('A model id is required.');
      return mergeRequestService.list(query);
    },
    enabled: query !== null,
    placeholderData: keepPreviousData,
  });
}

export function useMergeRequestQuery(id: string | null): UseQueryResult<MergeRequest> {
  return useQuery({
    queryKey: mergeRequestKeys.detail(id ?? ''),
    queryFn: () => {
      if (id === null) throw new Error('A merge request id is required.');
      return mergeRequestService.getById(id);
    },
    enabled: id !== null,
  });
}

export function useMergeRequestAuditTrailQuery(
  id: string | null
): UseQueryResult<readonly MergeRequestAuditEntry[]> {
  return useQuery({
    queryKey: mergeRequestKeys.auditTrail(id ?? ''),
    queryFn: () => {
      if (id === null) throw new Error('A merge request id is required.');
      return mergeRequestService.auditTrail(id);
    },
    enabled: id !== null,
  });
}

/** Every action returns the fresh request, so the cache is replaced and the trail refetched. */
function useAction<TInput>(
  id: string,
  run: (input: TInput) => Promise<MergeRequest>
): UseMutationResult<MergeRequest, Error, TInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (request) => {
      queryClient.setQueryData(mergeRequestKeys.detail(id), request);
      void queryClient.invalidateQueries({ queryKey: mergeRequestKeys.auditTrail(id) });
      void queryClient.invalidateQueries({ queryKey: ['mergeRequests', 'list'] });
    },
    // A 409 means someone else changed the request first; reload rather than show stale state.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: mergeRequestKeys.detail(id) });
    },
  });
}

export const useAddCommentMutation = (id: string): UseMutationResult<MergeRequest, Error, string> =>
  useAction(id, (text: string) => mergeRequestService.addComment(id, text));

export const useWithdrawMutation = (
  id: string
): UseMutationResult<MergeRequest, Error, string | undefined> =>
  useAction(id, (reason: string | undefined) => mergeRequestService.withdraw(id, reason));

export const useReviewDecisionMutation = (
  id: string
): UseMutationResult<MergeRequest, Error, DecisionInput> =>
  useAction(id, (input: DecisionInput) => mergeRequestService.reviewDecision(id, input));

export const useAuthorizerDecisionMutation = (
  id: string
): UseMutationResult<MergeRequest, Error, DecisionInput> =>
  useAction(id, (input: DecisionInput) => mergeRequestService.authorizerDecision(id, input));

export function useModelBaselinesQuery(modelId: string | null): UseQueryResult<ModelBaselines> {
  return useQuery({
    queryKey: mergeRequestKeys.baselines(modelId ?? ''),
    queryFn: () => {
      if (modelId === null) throw new Error('A model id is required.');
      return mergeRequestService.baselines(modelId);
    },
    enabled: modelId !== null,
  });
}

export function useParticipantsQuery(modelId: string | null): UseQueryResult<readonly Participant[]> {
  return useQuery({
    queryKey: mergeRequestKeys.participants(modelId ?? ''),
    queryFn: () => {
      if (modelId === null) throw new Error('A model id is required.');
      return mergeRequestService.participants(modelId);
    },
    enabled: modelId !== null,
  });
}

export function useMergeRequestDiffQuery(
  id: string | null,
  query: DiffQuery
): UseQueryResult<MergeRequestDiff> {
  return useQuery({
    queryKey: mergeRequestKeys.diff(id ?? '', query),
    queryFn: () => {
      if (id === null) throw new Error('A merge request id is required.');
      return mergeRequestService.diff(id, query);
    },
    enabled: id !== null,
    placeholderData: keepPreviousData,
  });
}

/** A revert changes what is current, so every list and the baselines are reloaded. */
export function useRevertMutation(modelId: string): UseMutationResult<ModelBaselines, Error, RevertInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RevertInput) => mergeRequestService.revert(modelId, input),
    onSuccess: (baselines) => {
      queryClient.setQueryData(mergeRequestKeys.baselines(modelId), baselines);
      void queryClient.invalidateQueries({ queryKey: ['mergeRequests', 'list'] });
    },
    // A 409 means the baseline moved first; show the real state.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: mergeRequestKeys.baselines(modelId) });
    },
  });
}

export function useCreateProductionRequestMutation(): UseMutationResult<
  MergeRequest,
  Error,
  ProductionRequestInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductionRequestInput) => mergeRequestService.createProductionRequest(input),
    onSuccess: (request) => {
      queryClient.setQueryData(mergeRequestKeys.detail(request.id), request);
      void queryClient.invalidateQueries({ queryKey: ['mergeRequests', 'list'] });
    },
  });
}

export const useFinalizeRetryMutation = (id: string): UseMutationResult<MergeRequest, Error, void> =>
  useAction(id, () => mergeRequestService.retryFinalize(id));

export function useDiffRetryMutation(id: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => mergeRequestService.retryDiff(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mergeRequests', 'diff', id] });
      void queryClient.invalidateQueries({ queryKey: mergeRequestKeys.detail(id) });
    },
  });
}
